import { useParams, useNavigate } from 'react-router-dom';
import { useMemo } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import VideoCard from '@/components/feature/VideoCard';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { categories, videos } from '@/mocks/data';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();

  const category = useMemo(() => categories.find((c) => c.slug === slug), [slug]);
  const categoryVideos = useMemo(
    () => videos.filter((v) => v.categorySlug === slug),
    [slug],
  );

  if (!category) {
    return (
      <>
        <ScrollToTop />
        <Navbar />
        <div className="min-h-screen flex items-center justify-center pt-16">
          <div className="text-center">
            <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4">
              <i className="ri-emotion-sad-line text-4xl text-foreground-400"></i>
            </div>
            <h1 className="text-xl font-bold text-foreground-950 mb-2">分类不存在</h1>
            <button
              onClick={() => navigate('/')}
              className="text-primary-500 hover:underline text-sm cursor-pointer"
            >
              返回首页
            </button>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <ScrollToTop />
      <Navbar />
      <main className="pt-20 pb-4 min-h-screen">
        <div className="max-w-[1400px] mx-auto px-4 md:px-6">
          <Breadcrumb
            items={[
              { label: '首页', path: '/' },
              { label: category.name },
            ]}
          />

          <div className="mt-6 mb-8">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600">
                <i className={`${category.icon} text-lg`}></i>
              </div>
              <h1 className="text-2xl md:text-3xl font-bold font-heading text-foreground-950">
                {category.name}
              </h1>
            </div>
            <p className="text-sm text-foreground-500 ml-13">{category.description}</p>
          </div>

          <div className="flex items-center gap-2 mb-6 flex-wrap">
            {categories.map((cat) => (
              <button
                key={cat.id}
                onClick={() => { navigate(`/category/${cat.slug}`); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                  cat.slug === slug
                    ? 'bg-primary-500 text-white'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200/70 border border-background-200/70'
                }`}
              >
                {cat.name}
              </button>
            ))}
          </div>

          {categoryVideos.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mb-10">
              {categoryVideos.map((video) => (
                <VideoCard key={video.id} {...video} />
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <i className="ri-inbox-line text-4xl text-foreground-400"></i>
              </div>
              <p className="text-foreground-500">该分类暂无视频内容</p>
            </div>
          )}
        </div>
      </main>
      <Footer />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'CollectionPage',
            name: `${category.name} - 黑料视频`,
            description: category.description,
            url: `https://heiliaoshipin.com/category/${category.slug}`,
            isPartOf: {
              '@type': 'WebSite',
              name: '黑料视频',
              url: 'https://heiliaoshipin.com/',
            },
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              {
                '@type': 'ListItem',
                position: 1,
                name: '首页',
                item: 'https://heiliaoshipin.com/',
              },
              {
                '@type': 'ListItem',
                position: 2,
                name: category.name,
              },
            ],
          }),
        }}
      />
    </>
  );
}